import React, {Component, ReactNode} from 'react'
import AssocManagementRepository from '../assocDirector/AssocManagementRepository'
import AdminGeneralMeetingRepo, {
    GeneralMeetingFile,
    GeneralMeetingRequestForUpdate,
    GetGeneralMeetingInfoResponse,
    ProposalByAssocRequest,
} from './AdminGeneralMeetingRepo'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {AssocOfCondominium} from '../assocDirector/model/AssocOfCondominium'
import {
    AdminGeneralMeetingInputForm,
    canRegisterGeneralMeeting,
    canSaveAsDraftGeneralMeeting,
    GeneralMeetingFormDataSet,
    ProposalByAssocFormDataSet,
} from './AdminGeneralMeetingInputForm'
import {AVAILABLE_OR_NOT, GENERAL_MEETING_STATUS, MESSAGE_TYPE} from '../../CodeMaster'
import {NoSelectedValue} from '../../componentHelper/selectHelper'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'

import {NatFileRepo} from '../../__NoAutomatedTests/components/files/repository/NatFileRepo'
import {Nullable} from '../../app/TypeHelpers'
import {AdminDefaultButton, AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import styled from 'styled-components'
import {RightRowFlexContainer, RowFlexContainer} from '../../styledComponents/FlexComponents'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import {PageFotterAreaLeft, PageFotterAreaRight} from '../shared/hoc/withAdminPageTemplate'
import {BackLink} from '../../styledComponents/adminPage/Links'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {createUUID} from '../../stringHelper/stringHelpers'
import {Messages} from '../../Messages'
import {Information} from '../../network/response/Information'
import Time from '../../dateHelpers/Time'
import {HttpError} from '../../network/errors/HttpError'

interface Props {
    condominiumCd: string
    generalMeetingId: number
    assocManagementRepo: AssocManagementRepository
    adminGeneralMeetingRepo: AdminGeneralMeetingRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    natFileRepo: NatFileRepo
    eventEmitter: EventEmitterExAdmin
}

interface State {
    version: number
    assocList: AssocOfCondominium[]
    generalMeetingFormDataSet: GeneralMeetingFormDataSet
    beforeGeneralMeetingStatus: Nullable<GENERAL_MEETING_STATUS>
    deleteFileNumberList: number[]
    currentDate: Date
}

class AdminGeneralMeetingEditPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            version: 0,
            assocList: [],
            generalMeetingFormDataSet: {
                generalMeetingName: '',
                selectedAssocIdList: [],
                generalMeetingDate: null,
                startHour: NoSelectedValue,
                startMinute: NoSelectedValue,
                votingPreferenceDeadline: null,
                questionAvailability: AVAILABLE_OR_NOT.AVAILABLE.value,
                questionDeadline: null,
                generalMeetingFileList: [],
                proposalFormDataSetList: [],
                noticeContent: '',
                releaseDate: null,
            },
            beforeGeneralMeetingStatus: null,
            deleteFileNumberList: [],
            currentDate: new Date(),
        }

        this.handleChangeGeneralMeetingFormDataSet = this.handleChangeGeneralMeetingFormDataSet.bind(this)
        this.handleDeleteExistingFile = this.handleDeleteExistingFile.bind(this)
        this.handleCancelClick = this.handleCancelClick.bind(this)
        this.handleClickSaveAsDraftButton = this.handleClickSaveAsDraftButton.bind(this)
        this.handleSaveAsDraftOkClick = this.handleSaveAsDraftOkClick.bind(this)
        this.handleClickRegisterButton = this.handleClickRegisterButton.bind(this)
        this.handleBeforeReleaseOkClick = this.handleBeforeReleaseOkClick.bind(this)
        this.handleAfterReleaseOkClick = this.handleAfterReleaseOkClick.bind(this)
    }

    static formatDateToString(date: Nullable<Date>): Nullable<string> {
        if (date === null) return null
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
    }

    async componentDidMount(): Promise<void> {
        const {
            eventEmitter,
            assocManagementRepo,
            currentDateTimeRepo,
            adminGeneralMeetingRepo,
            asyncCallFunction,
            condominiumCd,
            generalMeetingId,
            navigate,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const assocList: Nullable<AssocOfCondominium[]> =
                await asyncCallFunction(
                    assocManagementRepo.getAssocList.bind(assocManagementRepo, condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )
            if (assocList === null || assocList.length === 0) {
                navigate('/admin/error')
                return
            }
            this.setState({assocList})

            const currentDateTimeResponse = await NatAsyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                navigate,
            )
            if (currentDateTimeResponse !== null && currentDateTimeResponse.details !== null) {
                const currentDate: Date = new Date(currentDateTimeResponse.details.currentDateTime)
                this.setState({currentDate})
            }

            const response: Nullable<GetGeneralMeetingInfoResponse> =
                await asyncCallFunction(
                    adminGeneralMeetingRepo.getGeneralMeetingInfo.bind(
                        adminGeneralMeetingRepo,
                        condominiumCd,
                        generalMeetingId,
                    ),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )
            if (response === null) {
                navigate('/admin/error')
                return
            }

            const assocIdList = response.details.assocIdNameList.map(item => item.assocId)
            const startHour = response.details.startTime !== null
                ? String(parseInt(response.details.startTime.split(':')[0], 10)) : NoSelectedValue
            const startMinute = response.details.startTime !== null
                ? String(parseInt(response.details.startTime.split(':')[1], 10)) : NoSelectedValue
            const questionAvailability = response.details.questionAvailability !== null
                ? response.details.questionAvailability.value : AVAILABLE_OR_NOT.AVAILABLE.value
            const generalMeetingFileList: GeneralMeetingFile[] = response.details.fileList.map(file => ({
                fileNumber: file.fileNumber,
                fileName: file.fileName,
                fileData: file.fileDownloadPath,
                fileSize: file.fileSizeKB * 1024,
            }))
            const proposalFormDataSetList: ProposalByAssocFormDataSet[] =
                response.details.proposalList.map((assoc) => ({
                    assocId: assoc.assocId,
                    proposalDetailInfoFormDataSetList: assoc.generalMeetingProposalList.map((detail) => ({
                        proposalUID: createUUID(),
                        proposalNumber: detail.proposalNumber,
                        resolutionType: detail.resolutionType !== null
                            ? detail.resolutionType.value : null,
                        proposalName: detail.proposalName ?? '',
                    })),
                }))

            this.setState({
                version: response.details.version,
                generalMeetingFormDataSet: {
                    generalMeetingName: response.details.generalMeetingName ?? '',
                    selectedAssocIdList: assocIdList,
                    generalMeetingDate: response.details.generalMeetingDate,
                    startHour: startHour,
                    startMinute: startMinute,
                    votingPreferenceDeadline: response.details.votingPreferenceDeadline,
                    questionAvailability: questionAvailability,
                    questionDeadline: response.details.questionDeadline,
                    generalMeetingFileList: generalMeetingFileList,
                    proposalFormDataSetList: proposalFormDataSetList,
                    noticeContent: response.details.noticeContent ?? '',
                    releaseDate: response.details.releaseDate,
                },
                beforeGeneralMeetingStatus: response.details.generalMeetingStatus,
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleChangeGeneralMeetingFormDataSet(generalMeetingFormDataSet: GeneralMeetingFormDataSet): void {
        this.setState({generalMeetingFormDataSet})
    }

    handleDeleteExistingFile(fileNumber: number): void {
        this.setState(prevState => ({
            deleteFileNumberList: [...prevState.deleteFileNumberList, fileNumber],
        }))
    }

    handleCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleClickSaveAsDraftButton(): Promise<void> {
        await this.props.eventEmitter.showDialog({
            title: '確認',
            errorLevel: Messages.WGMTA0001.errorLevel,
            message: Messages.WGMTA0001.toString(),
            buttonProps: [
                {
                    isDefault: false,
                    buttonLabel: 'キャンセル',
                    buttonHandler: this.handleCancelClick,
                },
                {
                    isDefault: false,
                    buttonLabel: 'OK',
                    buttonHandler: this.handleSaveAsDraftOkClick,
                },
            ],
        })
    }

    async handleSaveAsDraftOkClick(): Promise<void> {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
            generalMeetingId,
            navigate,
        } = this.props
        const {
            generalMeetingFormDataSet,
            deleteFileNumberList,
            version,
        } = this.state

        eventEmitter.closeDialog()
        eventEmitter.clearBroadcastedErrors()

        await eventEmitter.showLoading()
        let resultGeneralMeetingId: number
        let initialMessage: HeadingAreaMessageParams
        try {
            if (!canSaveAsDraftGeneralMeeting(generalMeetingFormDataSet)) return

            const request = this.createGeneralMeetingRequestForUpdate(
                generalMeetingFormDataSet,
                deleteFileNumberList,
                GENERAL_MEETING_STATUS.DRAFT,
                version
            )

            const result = await asyncCallFunction(
                adminGeneralMeetingRepo.updateGeneralMeeting.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                    request,
                ),
                async (httpErrorOrNetworkError) => {
                    if (
                        httpErrorOrNetworkError instanceof HttpError &&
                        httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.EGMTA0011.id
                    ) {
                        // 公開中に関するエラーは、総会詳細ページに遷移して、エラー内容を表示する
                        const {title, message, messageId} = httpErrorOrNetworkError.responseBody.requestError
                        navigate(
                            `/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
                            {
                                state: {
                                    initialMessage: {
                                        title,
                                        messageLevel: MESSAGE_TYPE.ERROR,
                                        detail: message,
                                    },
                                },
                            },
                        )
                    } else {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    }
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('総会')},
            )
            if (!result) return

            resultGeneralMeetingId = result.details
            const newFiles = generalMeetingFormDataSet.generalMeetingFileList.filter(f => f.fileNumber === null)
            initialMessage = await this.uploadGeneralMeetingFiles(
                resultGeneralMeetingId,
                newFiles,
                result.information,
                Messages.IOPCC0030.toString('総会', '下書き'),
            )
        } finally {
            eventEmitter.closeLoading()
        }

        navigate(
            `/admin/condominiums/${condominiumCd}/general-meetings/${resultGeneralMeetingId!}`,
            {
                state: {
                    initialMessage: initialMessage!,
                },
            },
        )

    }

    async handleBeforeReleaseOkClick(): Promise<void> {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
            generalMeetingId,
            navigate,
        } = this.props
        const {
            generalMeetingFormDataSet,
            deleteFileNumberList,
            version,
        } = this.state

        eventEmitter.closeDialog()
        eventEmitter.clearBroadcastedErrors()

        await eventEmitter.showLoading()
        let resultGeneralMeetingId: number
        let initialMessage: HeadingAreaMessageParams
        try {
            if (!canRegisterGeneralMeeting(generalMeetingFormDataSet)) return

            const request = this.createGeneralMeetingRequestForUpdate(
                generalMeetingFormDataSet,
                deleteFileNumberList,
                GENERAL_MEETING_STATUS.BEFORE_RELEASE,
                version
            )

            const result = await asyncCallFunction(
                adminGeneralMeetingRepo.updateGeneralMeeting.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                    request,
                ),
                async (httpErrorOrNetworkError) => {
                    if (
                        httpErrorOrNetworkError instanceof HttpError &&
                        httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.EGMTA0011.id
                    ) {
                        // 公開中に関するエラーは、総会詳細ページに遷移して、エラー内容を表示する
                        const {title, message, messageId} = httpErrorOrNetworkError.responseBody.requestError
                        navigate(
                            `/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
                            {
                                state: {
                                    initialMessage: {
                                        title,
                                        messageLevel: MESSAGE_TYPE.ERROR,
                                        detail: message,
                                    },
                                },
                            },
                        )
                    } else {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    }
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('総会')},
            )
            if (!result) return

            resultGeneralMeetingId = result.details
            const newFiles = generalMeetingFormDataSet.generalMeetingFileList.filter(f => f.fileNumber === null)
            initialMessage = await this.uploadGeneralMeetingFiles(
                resultGeneralMeetingId,
                newFiles,
                result.information,
                Messages.IOPCC0030.toString('総会', '下書き'),
            )

            // 全てファイルのアップロードに成功した場合は、ステータスを下書きから公開予約に更新する
            if (initialMessage.messageLevel !== MESSAGE_TYPE.ERROR) {
                try {
                    // AsyncCallFunctionによる標準の通信エラー処理を行わない
                    // 原因はなんであれ公開予約ではなく、下書き保存になったことをユーザーに伝える必要があるため、全ての例外を捕捉する
                    await adminGeneralMeetingRepo.updateGeneralMeetingStatus(
                        condominiumCd,
                        generalMeetingId,
                        {
                            generalMeetingStatus: GENERAL_MEETING_STATUS.BEFORE_RELEASE.value,
                            version: version + 1,
                        }
                    )
                } catch (e) {
                    initialMessage = {
                        title: Messages.IOPCC0030.toString('総会', '下書き'),
                        messageLevel: MESSAGE_TYPE.ERROR,
                        detail: Messages.ESYCC0020.toString(),
                    }
                }
            }
        } finally {
            eventEmitter.closeLoading()
        }

        navigate(
            `/admin/condominiums/${condominiumCd}/general-meetings/${resultGeneralMeetingId!}`,
            {
                state: {
                    initialMessage: initialMessage!,
                },
            },
        )
    }

    async handleAfterReleaseOkClick(): Promise<void> {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
            generalMeetingId,
            navigate,
        } = this.props
        const {
            generalMeetingFormDataSet,
            deleteFileNumberList,
            version,
        } = this.state

        eventEmitter.closeDialog()
        eventEmitter.clearBroadcastedErrors()

        await eventEmitter.showLoading()
        let resultGeneralMeetingId: number
        let initialMessage: HeadingAreaMessageParams
        try {
            if (!canRegisterGeneralMeeting(generalMeetingFormDataSet)) return

            const request = this.createGeneralMeetingRequestForUpdate(
                generalMeetingFormDataSet,
                deleteFileNumberList,
                GENERAL_MEETING_STATUS.AFTER_RELEASE,
                version
            )

            const result = await asyncCallFunction(
                adminGeneralMeetingRepo.updateGeneralMeeting.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                    request,
                ),
                async (httpErrorOrNetworkError) => {
                    if (
                        httpErrorOrNetworkError instanceof HttpError &&
                        httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.EGMTA0012.id
                    ) {
                        // 公開中止に関するエラーは、総会詳細ページに遷移して、エラー内容を表示する
                        const {title, message, messageId} = httpErrorOrNetworkError.responseBody.requestError
                        navigate(
                            `/admin/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
                            {
                                state: {
                                    initialMessage: {
                                        title,
                                        messageLevel: MESSAGE_TYPE.ERROR,
                                        detail: message,
                                    },
                                },
                            },
                        )
                    } else {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    }
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('総会')},
            )
            if (!result) return

            resultGeneralMeetingId = result.details
            const newFiles = generalMeetingFormDataSet.generalMeetingFileList.filter(f => f.fileNumber === null)
            initialMessage = await this.uploadGeneralMeetingFiles(
                resultGeneralMeetingId,
                newFiles,
                result.information,
                Messages.IOPCC0008.toString('総会'),
            )
        } finally {
            eventEmitter.closeLoading()
        }

        navigate(
            `/admin/condominiums/${condominiumCd}/general-meetings/${resultGeneralMeetingId!}`,
            {
                state: {
                    initialMessage: initialMessage!,
                },
            },
        )
    }

    createGeneralMeetingRequestForUpdate(
        generalMeetingFormDataSet: GeneralMeetingFormDataSet,
        deleteFileNumberList: number[],
        generalMeetingStatus: GENERAL_MEETING_STATUS,
        version: number,
    ): GeneralMeetingRequestForUpdate {
        let startTime: Nullable<string> = null
        if (generalMeetingFormDataSet.startHour !== NoSelectedValue
            && generalMeetingFormDataSet.startMinute !== NoSelectedValue
        ) {
            startTime = new Time(
                Number(generalMeetingFormDataSet.startHour),
                Number(generalMeetingFormDataSet.startMinute),
            ).toString()
        }

        let trimedProposalFormDataSetList = generalMeetingFormDataSet.proposalFormDataSetList
            .filter(proposalByAssocFormDataSet =>
                generalMeetingFormDataSet.selectedAssocIdList.includes(proposalByAssocFormDataSet.assocId))

        if (generalMeetingStatus === GENERAL_MEETING_STATUS.BEFORE_RELEASE) {
            trimedProposalFormDataSetList = trimedProposalFormDataSetList.map((proposalByAssocFormDataSet) => {
                const list = proposalByAssocFormDataSet.proposalDetailInfoFormDataSetList
                let end = list.length
                while (end > 0 && list[end - 1].resolutionType === null && list[end - 1].proposalName === '') {
                    end--
                }
                return {...proposalByAssocFormDataSet, proposalDetailInfoFormDataSetList: list.slice(0, end),}
            })
        }

        const proposalList: ProposalByAssocRequest[] = trimedProposalFormDataSetList.map(
            proposal => ({
                assocId: proposal.assocId,
                proposalDetailInfoList: proposal.proposalDetailInfoFormDataSetList.map(
                    detail => ({
                        proposalNumber: detail.proposalNumber,
                        resolutionType: detail.resolutionType,
                        proposalName: detail.proposalName === '' ? null : detail.proposalName,
                    }),
                ),
            }),
        )

        const fileNameList = generalMeetingFormDataSet.generalMeetingFileList.map(file => file.fileName)

        return {
            generalMeetingName: generalMeetingFormDataSet.generalMeetingName === ''
                ? null : generalMeetingFormDataSet.generalMeetingName,
            assocIdList: generalMeetingFormDataSet.selectedAssocIdList,
            generalMeetingDate: AdminGeneralMeetingEditPage.formatDateToString(
                generalMeetingFormDataSet.generalMeetingDate),
            startTime,
            votingPreferenceDeadline: AdminGeneralMeetingEditPage.formatDateToString(
                generalMeetingFormDataSet.votingPreferenceDeadline),
            questionAvailability: generalMeetingFormDataSet.questionAvailability,
            questionDeadline: AdminGeneralMeetingEditPage.formatDateToString(
                generalMeetingFormDataSet.questionDeadline),
            fileNameList,
            proposalList,
            noticeContent: generalMeetingFormDataSet.noticeContent === ''
                ? null : generalMeetingFormDataSet.noticeContent,
            releaseDate: AdminGeneralMeetingEditPage.formatDateToString(generalMeetingFormDataSet.releaseDate),
            generalMeetingStatusRequest: {
                generalMeetingStatus: generalMeetingStatus.value,
                version,
            },
            deleteFileNumberList,
        }
    }

    async uploadGeneralMeetingFiles(
        generalMeetingId: number,
        fileList: GeneralMeetingFile[],
        information: Information,
        errorTitle: string,
    ): Promise<HeadingAreaMessageParams> {
        const fileCount = fileList.length
        const errorFiles: string[] = []
        for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
            this.props.eventEmitter.changeLoadingMessage(
                `ファイルアップロード中... ${fileIndex + 1}/${fileCount}`
            )
            try {
                const file = fileList[fileIndex]
                const fileData: string = file.fileNumber === null ?
                    String(file.fileData).split(',')[1] : String(file.fileData)
                await this.props.adminGeneralMeetingRepo.registerGeneralMeetingFile(
                    this.props.condominiumCd,
                    generalMeetingId,
                    {
                        fileNumber: file.fileNumber,
                        fileName: file.fileName,
                        fileData: fileData,
                        fileSize: file.fileSize,
                    },
                )
            } catch (e) {
                errorFiles.push(fileList[fileIndex].fileName)
            }
        }
        if (errorFiles.length > 0) {
            return {
                title: errorTitle,
                messageLevel: MESSAGE_TYPE.ERROR,
                detail: '以下のファイル登録に失敗しています。編集画面より再度登録してください。',
                itemErrors: errorFiles,
            }
        }
        return {
            title: information.title ?? null,
            messageLevel: information.type,
            detail: information.message,
        }
    }

    async handleClickRegisterButton(): Promise<void> {
        const {
            beforeGeneralMeetingStatus,
            generalMeetingFormDataSet
        } = this.state
        const isAfterRelease = beforeGeneralMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE

        let dialogErrorLevel
        let dialogMessage
        if (isAfterRelease) {
            dialogErrorLevel = Messages.WGMTA0007.errorLevel
            dialogMessage = Messages.WGMTA0007.toString()
        } else {
            const releaseDate = generalMeetingFormDataSet.releaseDate
            if (releaseDate === null) return
            const releaseDateText = `${releaseDate.getFullYear()}年${releaseDate.getMonth() + 1}月${releaseDate.getDate()}日 午前0時頃`

            dialogErrorLevel = Messages.WGMTA0002.errorLevel
            dialogMessage = Messages.WGMTA0002.toString(releaseDateText, '午前8時頃')
        }

        await this.props.eventEmitter.showDialog({
            title: '確認',
            errorLevel: dialogErrorLevel,
            message: dialogMessage,
            buttonProps: [
                {
                    isDefault: false,
                    buttonLabel: 'キャンセル',
                    buttonHandler: this.handleCancelClick,
                },
                {
                    isDefault: false,
                    buttonLabel: 'OK',
                    buttonHandler: isAfterRelease
                        ? this.handleAfterReleaseOkClick
                        : this.handleBeforeReleaseOkClick
                },
            ],
        })
    }

    render(): ReactNode {
        const {
            beforeGeneralMeetingStatus,
            generalMeetingFormDataSet,
        } = this.state

        //  編集前総会情報がまだ取得できていない(総会ステータスがnullである)場合は何も表示しない
        if (beforeGeneralMeetingStatus === null) return null

        const isDraft = beforeGeneralMeetingStatus === GENERAL_MEETING_STATUS.DRAFT
        const isBeforeRelease = beforeGeneralMeetingStatus === GENERAL_MEETING_STATUS.BEFORE_RELEASE
        const isAfterRelease = beforeGeneralMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE

        const showDraftButton = isDraft || isBeforeRelease
        const showRegisterButton = isDraft || isBeforeRelease || isAfterRelease
        const draftButtonLabel = isDraft ? '下書き保存する' : '下書きに戻す'
        const registerButtonLabel = isAfterRelease ? '登録する' : '公開で予約する'

        return <>
            <HeaderArea>
                {showDraftButton
                    && <AdminDefaultButton
                        disabled={!canSaveAsDraftGeneralMeeting(generalMeetingFormDataSet)}
                        onClick={this.handleClickSaveAsDraftButton}
                    >
                        {draftButtonLabel}
                    </AdminDefaultButton>
                }
                {showRegisterButton
                    && <AdminPrimaryButton
                        disabled={!canRegisterGeneralMeeting(generalMeetingFormDataSet)}
                        onClick={this.handleClickRegisterButton}
                    >
                        {registerButtonLabel}
                    </AdminPrimaryButton>
                }
            </HeaderArea>
            <AdminGeneralMeetingInputForm
                condominiumCd={this.props.condominiumCd}
                assocList={this.state.assocList}
                generalMeetingFormDataSet={generalMeetingFormDataSet}
                currentDate={this.state.currentDate}
                eventEmitter={this.props.eventEmitter}
                onChangeGeneralMeetingFormDataSet={this.handleChangeGeneralMeetingFormDataSet}
                onDeleteExistingFile={this.handleDeleteExistingFile}
                natFileRepo={this.props.natFileRepo}
                asyncCallFunction={this.props.asyncCallFunction}
                isAfterRelease={isAfterRelease}
            />
            <FooterArea>
                <StyledPageFooterAreaRight>
                    {showDraftButton
                        && <AdminDefaultButton
                            disabled={!canSaveAsDraftGeneralMeeting(generalMeetingFormDataSet)}
                            onClick={this.handleClickSaveAsDraftButton}
                        >
                            {draftButtonLabel}
                        </AdminDefaultButton>
                    }
                    {showRegisterButton
                        && <AdminPrimaryButton
                            disabled={!canRegisterGeneralMeeting(generalMeetingFormDataSet)}
                            onClick={this.handleClickRegisterButton}
                        >
                            {registerButtonLabel}
                        </AdminPrimaryButton>
                    }
                </StyledPageFooterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/general-meetings/${this.props.generalMeetingId}`}
                    >
                        総会詳細へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </FooterArea>
        </>
    }
}

export default createAdminPage(AdminGeneralMeetingEditPage)

const HeaderArea = styled(RightRowFlexContainer)`
  gap: 10px;
  padding-top: 10px;
  padding-bottom: 15px;
`

const FooterArea = styled(RowFlexContainer)`
  justify-content: space-between;
  padding-top: 10px
`

const StyledPageFooterAreaRight = styled(PageFotterAreaRight)`
  gap: 10px;
`
